import React, {useEffect, useState} from "react";
import SectionHomeWrapper from "../common/wrappers/SectionHomeWrapper";
import {ProgressBar, Table, AlertVariant} from "react-unity";
import Paginator from "../common/tables/Paginator";
import {LinkTreeItem} from "../common/LinkTree";
import SubscriptionRequest from "../../models/entities/SubscriptionRequest";
import SubscriptionRequestsService from "../../services/SubscriptionRequestsService";
import { useNavigate, useParams } from "react-router-dom";
import VirtualMachineRequestTableRow from "./VirtualMachineRequestTableRow";
import VirtualMachineRequest from "../../models/entities/VirtualMachineRequest";
import './VirtualMachineRequestTableRow.css';
import { AlertBanner } from "../../models/interfaces/AlertBanner";
import AlertModal from "../common/modals/AlertModal";
import VirtualMachineRequestHistoryFilters from "./VirtualMachineRequestHistoryFilters";
import VirtualMachineRequestService from "../../services/VirtualMachineRequestService";

const VirtualMachineRequestHistory = () => {
	const {subsId} = useParams<{ subsId: string }>();
	const subscriptionId = parseInt(subsId);
	const [subscription, setSubscription] = useState<SubscriptionRequest>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [virtualMachineRequests, setVirtualMachineRequests] = useState<VirtualMachineRequest[]>([]);
	const [virtualMachineRequestsOnPage, setVirtualMachineRequestsOnPage] = useState<VirtualMachineRequest[]>([]);
	const [filteredVirtualMachineRequests, setFilteredRequests] = useState<VirtualMachineRequest[]>();
	const [endModal, setEndModal] = useState<AlertBanner>({ visible: false });
	const subscriptionService = new SubscriptionRequestsService();
	const virtualMachineRequestService = new VirtualMachineRequestService();
	const navigate = useNavigate();

	const getLinkTree = (): LinkTreeItem[] => {
		return (subscription?.id && [
			{text: 'Subscriptions', to: '/subscriptions'},
			{text: subscription?.createdSubscriptionName, to: `/subscriptions/${subscription?.id}`},
		]) || [];
	};

	const retreiveVirtualMachineRequests = async () => {
		try {
			setLoading(true);
			const sub = new SubscriptionRequest(await subscriptionService.getById(subscriptionId));
			setSubscription(sub);
			const vmrs = await virtualMachineRequestService.getAllVirtualMachinesInSubscription(sub.createdSubscriptionId);
			setVirtualMachineRequests(vmrs);
			setLoading(false);
		} catch (err) {
			handleEndModal('The virtual machines could not be retrieved.', 'error', 10000);
		}
	};

	const handleEndModal = (text: string, variant: AlertVariant, timeout: number) => {
		setEndModal({
			visible: true,
			text,
			variant,
		});
		setTimeout(() => endAndRedirect(), timeout);
	};

	const getFilterProps = () => {
		return {
			setFilteredRequests,
			retreiveVirtualMachineRequests,
			virtualMachineRequests
		};
	};

	const endAndRedirect = () => {
		setEndModal({ visible: false });
		navigate('/');
	};

	useEffect(() => {
		retreiveVirtualMachineRequests();
	}, []);

	return (
		<>
			<SectionHomeWrapper
				title={`Virtual Machine Requests`}
				linkTree={getLinkTree()}
			>
				{loading ?
					<ProgressBar
						label="Loading Virtual Machine Requests..."
						className="em-u-margin-top-half"
						indeterminate
						hideValueLabel
					/> :
					<>
						<VirtualMachineRequestHistoryFilters {...getFilterProps()} />
						<Table
							footer={
								<Paginator
									data={filteredVirtualMachineRequests}
									totalItems={virtualMachineRequests.length}
									onPageChange={(page) => {
										setVirtualMachineRequestsOnPage(page);
									}}
								/>
							}
						>
							<Table.Head>
								<Table.Head.Row>
									<Table.Head.Cell> # Id </Table.Head.Cell>
									<Table.Head.Cell> Requester </Table.Head.Cell>
									<Table.Head.Cell> Status </Table.Head.Cell>
									<Table.Head.Cell> Environment </Table.Head.Cell>
									<Table.Head.Cell> Operating System </Table.Head.Cell>
									<Table.Head.Cell> Patching Mode </Table.Head.Cell>
									<Table.Head.Cell> Performed on Date </Table.Head.Cell>
									<Table.Head.Cell> </Table.Head.Cell>
								</Table.Head.Row>
							</Table.Head>
							<Table.Body>
								{virtualMachineRequestsOnPage.map((vm) => (					
									<VirtualMachineRequestTableRow
										key={vm.id}
										request={vm}
										subsId={subscription.id}
									/>
								))}
							</Table.Body>
						</Table>
					</>}
			</SectionHomeWrapper>
			<AlertModal {...endModal} willTimeout={false} onClose={endAndRedirect} />
		</>
	);
};

export default VirtualMachineRequestHistory;